import React from "react";
import styled from "styled-components";
import {
  ArrowIcon,
  Button,
  colors,
  Popup,
  PopupHeader,
  Text,
  XIcon,
} from "@commonsku/styles";

const FormPopupActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 0.5rem;
`;

const FormPopupForm = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;
  overflow-y: auto;
`;

const FORM_POPUP_STYLE = {
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "flex-start",
  gap: "1rem",
};

const FORM_POPUP_CONTENT_STYLE = {
  display: "flex",
  flexFlow: "column nowrap",
  justifyContent: "space-between",
  flexGrow: 1,
  gap: "1rem",
  overflowY: "hidden",
} as React.CSSProperties;

interface FormPopupProps {
  isNew: boolean;
  isEditable: boolean;
  isValid: boolean;
  onClose: () => void;
  onSave?: () => Promise<void>;
  title: React.ReactNode;
  width?: string;
  height?: string;
  children: React.ReactNode;
  customActions?: React.ReactNode;
  zIndex?: number;
  overlayZIndex?: number;
  onLeftArrowClick?: () => void;
}

const StyledPopupHeader = styled(PopupHeader)<{ withLeftArrow?: boolean }>`
  font-size: 24px;
  font-weight: 600;
  line-height: 40px;
  display: flex;
  align-items: center;

  span {
    color: ${colors.neutrals["90"]};
    flex-grow: 1;
    margin-left: ${({ withLeftArrow }) => (withLeftArrow ? "16px" : "0px")};
    padding-top: 3px;
  }

  svg {
    cursor: pointer;
    font-size: 32px;
  }
`;

const StyledCloseButton = styled(XIcon)`
  margin-left: auto;
`;

interface PopupHeaderProps {
  title: React.ReactNode;
  onPopupClose: () => void;
  onLeftArrowClick?: () => void;
}

export const PopupHeaderWithCloseIcon = ({
  title,
  onPopupClose,
  onLeftArrowClick = null,
}: PopupHeaderProps) => (
  <StyledPopupHeader withLeftArrow={!!onLeftArrowClick}>
    {onLeftArrowClick && (
      <ArrowIcon
        size="large"
        direction="left"
        altText="back"
        onClick={onLeftArrowClick}
      />
    )}
    <Text>{title}</Text>
    <StyledCloseButton altText="Close" onClickCapture={onPopupClose} />
  </StyledPopupHeader>
);

export default function FormPopup({
  isNew,
  isEditable,
  isValid,
  onClose,
  onSave,
  title,
  width,
  height,
  children,
  customActions = null,
  onLeftArrowClick = null,
  ...zIndexProps
}: FormPopupProps) {
  const renderActions = () => {
    if (customActions) {
      return customActions;
    }
    return (
      <>
        {(isEditable || !onSave) && (
          <Button secondary onClick={onClose}>
            Cancel
          </Button>
        )}
        {!!onSave &&
          (isEditable ? (
            <Button disabled={!isValid} onClick={onSave}>
              {isNew ? "Create" : "Save"}
            </Button>
          ) : (
            <Button onClick={onClose}>Close</Button>
          ))}
      </>
    );
  };

  return (
    <Popup
      closeOnEsc
      closeOnClickOutside={false}
      onClose={onClose}
      width={width}
      height={height}
      style={FORM_POPUP_STYLE}
      popupContentStyle={FORM_POPUP_CONTENT_STYLE}
      header={
        <PopupHeaderWithCloseIcon
          title={title}
          onPopupClose={onClose}
          onLeftArrowClick={onLeftArrowClick}
        />
      }
      {...zIndexProps}
    >
      <FormPopupForm>{children}</FormPopupForm>
      <FormPopupActions>{renderActions()}</FormPopupActions>
    </Popup>
  );
}
